import type { ReactNode } from 'react'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/*
 * Building blocks of the public site in Encuentro (ALU-14). They only speak
 * the system: `en-` tokens, Geist, the 4-64 px spacing scale. Public covers
 * are the one place where type goes past the product's `display` token: the
 * brand manual sets 40-72 px for them ("Una voz tipográfica legible"), so
 * headings use Tailwind's 4xl-6xl steps (36-60 px) with the system's weight
 * and tracking.
 */

/** Page gutter and width: 16 px on the phone, 24 px from `md`, 1152 px wide. */
export function MarketingContainer({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={cn('mx-auto w-full max-w-6xl px-4 md:px-6', className)}>{children}</div>
}

/** The small line over a section title: a label in `electric` (a brand accent). */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-en-label font-semibold text-en-electric', className)}>{children}</p>
}

/**
 * A title in two parts, as the copy writes them ("Planes que crecen con" +
 * "tu comunidad."). The second part goes in `brand` blue: the classic italic
 * serif accent has no place in Encuentro (Geist only, no serif).
 */
export function TwoPartTitle({
  as: Tag = 'h2',
  lead,
  accent,
  className,
}: {
  as?: 'h1' | 'h2'
  lead: string
  accent?: string
  className?: string
}) {
  return (
    <Tag
      className={cn(
        'font-semibold tracking-tight text-balance text-en-ink [overflow-wrap:anywhere]',
        Tag === 'h1'
          ? 'text-4xl leading-tight sm:text-5xl lg:text-6xl'
          : 'text-3xl leading-tight md:text-4xl',
        className
      )}
    >
      {lead}
      {accent ? (
        <>
          {' '}
          <span className="text-en-brand">{accent}</span>
        </>
      ) : null}
    </Tag>
  )
}

/** Eyebrow, title and the line under it, as every section of the site opens. */
export function SectionIntro({
  eyebrow,
  title,
  accent,
  subtitle,
  align = 'center',
  className,
}: {
  eyebrow?: string
  title: string
  accent?: string
  subtitle?: string
  align?: 'center' | 'start'
  className?: string
}) {
  return (
    <div
      className={cn(
        'grid max-w-2xl gap-4',
        align === 'center' ? 'mx-auto justify-items-center text-center' : 'text-left',
        className
      )}
    >
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <TwoPartTitle lead={title} accent={accent} />
      {subtitle ? <p className="text-lg leading-relaxed text-en-ink-muted">{subtitle}</p> : null}
    </div>
  )
}

/**
 * A call to action that is a link. `primary` is the ink button of the system;
 * `secondary` the white one (also the button on a `brand` band, where white on
 * blue is the contrast the manual asks for). `onBrand` turns the focus ring
 * white: electric on blue is 1.67:1.
 */
export function CtaLink({
  href,
  children,
  variant = 'primary',
  arrow = false,
  onBrand = false,
  external = false,
  className,
}: {
  href: string
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'ghost'
  arrow?: boolean
  onBrand?: boolean
  /** Same-page anchors and non-Next routes use a plain `<a>`. */
  external?: boolean
  className?: string
}) {
  const content = (
    <>
      {children}
      {arrow ? <ArrowRight aria-hidden="true" /> : null}
    </>
  )
  return (
    <Button
      look="encuentro"
      variant={variant}
      asChild
      className={cn('px-6', onBrand && 'focus-visible:outline-en-bg', className)}
    >
      {external ? <a href={href}>{content}</a> : <Link href={href}>{content}</Link>}
    </Button>
  )
}

/** A product screenshot on a card: `surface`, `radius-lg`, `shadow-card`. */
export function ScreenshotCard({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-en-lg bg-en-surface shadow-en-card ring-1 ring-en-line',
        className
      )}
    >
      {children}
    </div>
  )
}

/** A portrait phone capture in a device frame (ink bezel), at most 290 px wide. */
export function PhoneFrame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('mx-auto w-full max-w-72', className)}>
      <div className="overflow-hidden rounded-[2.5rem] border-8 border-en-ink bg-en-ink shadow-en-overlay">
        {children}
      </div>
    </div>
  )
}
